/**
 * Maps Elasticsearch dashboard formatter output (saved-query `data` arrays) into the legacy
 * overview chart shapes expected by echarts components (event_wise, Entry_Exits, etc.).
 */
export function elasticQueryDataToLegacyChartShape(
  graphType: string,
  raw: unknown,
): Record<string, unknown> {
  const t = graphType.trim().toLowerCase();
  if (!Array.isArray(raw)) return {};

  if (t === "pie") {
    const pts = raw as { label?: string; value?: number }[];
    const categories = pts.map((p) => String(p.label ?? "Unknown"));
    const series = pts.map((p) => Number(p.value ?? 0));
    return { event_wise: { categories, series } };
  }

  if (t === "bar") {
    const pts = raw as { x?: string; y?: number }[];
    const categories = pts.map((p) => String(p.x ?? ""));
    const data = pts.map((p) => Number(p.y ?? 0));
    return {
      Student_Footfall: {
        categories,
        series: [{ name: "Count", data }],
      },
    };
  }

  if (t === "line") {
    const pts = raw as Record<string, unknown>[];
    if (pts.length === 0) return { Entry_Exits: { categories: [], series: [] } };
    const first = pts[0];
    const numericKeys = Object.keys(first).filter(
      (k) => k !== "x" && k !== "label" && k !== "name" && typeof first[k] === "number",
    );
    const categories = pts.map((p) => String(p.x ?? p.label ?? p.name ?? ""));
    if (numericKeys.length > 0) {
      const series = numericKeys.map((name) => ({
        name,
        data: pts.map((p) => Number(p[name] ?? 0)),
      }));
      return { Entry_Exits: { categories, series } };
    }
    const data = pts.map((p) => Number(p.y ?? p.value ?? 0));
    return {
      Entry_Exits: {
        categories,
        series: [{ name: "Count", data }],
      },
    };
  }

  if (t === "stack") {
    const pts = raw as { x?: string; series?: Array<{ name: string; value: number }> }[];
    const categories = pts.map((p) => String(p.x ?? ""));
    const nameSet = new Set<string>();
    pts.forEach((p) => (p.series ?? []).forEach((s) => nameSet.add(s.name)));
    const names = Array.from(nameSet);
    const series = names.map((name) => ({
      name,
      data: pts.map((row) => {
        const hit = (row.series ?? []).find((s) => s.name === name);
        return Number(hit?.value ?? 0);
      }),
    }));
    return { event_analysis: { categories, series } };
  }

  if (t === "slot") {
    const pts = raw as { slot?: string; series?: Array<{ name: string; value: number }> }[];
    const categories: string[] = [];
    const numbers: number[] = [];
    for (const p of pts) {
      categories.push(String(p.slot ?? ""));
      const sum = (p.series ?? []).reduce((acc, s) => acc + Number(s.value ?? 0), 0);
      numbers.push(sum);
    }
    const total = numbers.reduce((a, b) => a + b, 0) || 1;
    const series = numbers.map((n) => (n / total) * 100);
    return { slot_wise: { categories, series, numbers } };
  }

  return {};
}
